import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Fade,
  Slide,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  CardMembership as MembershipIcon,
  School as BackgroundIcon,
  VolunteerActivism as VolunteerIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import Header from './components/Header';
import FormSection from './components/FormSection';
import PersonalInfoForm from './components/PersonalInfoForm';
import MembershipForm from './components/MembershipForm';
import BackgroundInfoForm from './components/BackgroundInfoForm';
import ActivitiesForm from './components/ActivitiesForm';
import SuccessModal from './components/SuccessModal';
import ConfirmationDialog from './components/ConfirmationDialog';
import { useForm } from './hooks/useForm';
import { submitForm } from './services/api';

function App() {
  const { formData, isSubmitting, setIsSubmitting, errors, handleChange, validateForm, resetForm } = useForm();
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Define form steps
  const steps = [
    {
      label: 'Persoonlijke Gegevens',
      subtitle: 'Vul hier uw basisgegevens in',
      icon: <PersonIcon />,
      color: '#e53935',
      component: <PersonalInfoForm formData={formData} handleChange={handleChange} errors={errors} />,
      validate: () => {
        const stepErrors = {};
        ['naam', 'adres', 'geboortedatum', 'telefoon', 'email'].forEach(field => {
          if (errors[field]) stepErrors[field] = errors[field];
        });
        return Object.keys(stepErrors).length === 0;
      }
    },
    {
      label: 'Lidmaatschap & Contributie',
      subtitle: 'Kies uw lidmaatschapstype en contributie',
      icon: <MembershipIcon />,
      color: '#4caf50',
      component: <MembershipForm formData={formData} handleChange={handleChange} errors={errors} />,
      validate: () => {
        const stepErrors = {};
        if (errors.lidmaatschap) stepErrors.lidmaatschap = errors.lidmaatschap;
        if (formData.lidmaatschap === 'vrijwillig' && errors.vrijwilligeBijdrage) {
          stepErrors.vrijwilligeBijdrage = errors.vrijwilligeBijdrage;
        }
        return Object.keys(stepErrors).length === 0;
      }
    },
    {
      label: 'Achtergrond Informatie',
      subtitle: 'Vertel ons meer over uzelf',
      icon: <BackgroundIcon />,
      color: '#1976d2',
      component: <BackgroundInfoForm formData={formData} handleChange={handleChange} errors={errors} />,
      validate: () => true // No required fields in this step
    },
    {
      label: 'Activiteiten',
      subtitle: 'Waar wilt u aan bijdragen?',
      icon: <VolunteerIcon />,
      color: '#ff9800',
      component: <ActivitiesForm formData={formData} handleChange={handleChange} errors={errors} />,
      validate: () => true // No required fields in this step
    }
  ];

  // Handle step navigation
  const handleNext = () => {
    // Validate current step
    if (steps[activeStep].validate()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      // Show validation error
      setSubmitStatus({ 
        type: 'error', 
        message: 'Controleer de verplichte velden en probeer opnieuw.' 
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step) => {
    // Only allow going back to previous steps or current step
    if (step <= activeStep) {
      setActiveStep(step);
    }
  };
  
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Validate form before showing confirmation
    if (!validateForm()) {
      setSubmitStatus({
        type: 'error',
        message: 'Controleer de verplichte velden en probeer opnieuw.'
      });
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' }); // Clear any existing messages
    setShowConfirmDialog(false);

    try {
      await submitForm(formData);

      // Clear any error states and show success modal
      setSubmitStatus({ type: '', message: '' });
      setShowSuccessModal(true);

      // Reset form and step after a small delay to ensure state is clean
      setTimeout(() => {
        resetForm();
        setActiveStep(0);
      }, 100);

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Er is een onbekende fout opgetreden.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth={false} sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Fade in timeout={800}>
          <Box>
            {submitStatus.message && !showSuccessModal && (
              <Slide direction="down" in={!!submitStatus.message} mountOnEnter unmountOnExit>
                <Alert 
                  severity={submitStatus.type} 
                  sx={{ 
                    mb: 4,
                    borderRadius: '12px',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                  }}
                  onClose={() => setSubmitStatus({ type: '', message: '' })}
                >
                  {submitStatus.message}
                </Alert>
              </Slide>
            )}

            <Paper 
              elevation={0}
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 16px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                width: { xs: '100%', md: '90%', lg: '80%' },
                maxWidth: '1200px',
                mx: 'auto',
              }}
            >
              <Box component="form" onSubmit={handleSubmit} onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                  e.preventDefault();
                }
              }} sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepButton onClick={() => handleStepClick(index)}>
                        <StepLabel 
                          StepIconProps={{
                            sx: { color: index === activeStep ? step.color : '' }
                          }}
                        >
                          {step.label}
                        </StepLabel>
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>

                <Box sx={{ mt: 4, mb: 4 }}>
                  <FormSection
                    title={steps[activeStep].label}
                    subtitle={steps[activeStep].subtitle}
                    icon={steps[activeStep].icon}
                    color={steps[activeStep].color}
                  >
                    {steps[activeStep].component}
                  </FormSection>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mt: 4, 
                  p: 2,
                  width: '100%',
                  mx: 'auto',
                  background: 'rgba(248, 249, 250, 0.8)', 
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <Box>
                    <Button
                      variant="outlined"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      startIcon={<BackIcon />}
                    >
                      Vorige
                    </Button>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                    Stap {activeStep + 1} van {steps.length}
                  </Typography>
                  <Box>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckIcon />}
                      >
                        {isSubmitting ? 'Verzenden...' : 'Aanmelden'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        endIcon={<NextIcon />}
                      >
                        Volgende
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
      
      {/* Footer */}
      <Box
        sx={{
          background: 'rgba(44, 62, 80, 0.95)',
          color: 'white',
          py: 4,
          textAlign: 'center',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            2025 SamenWerkt Wijk bij Duurstede • Lokale politiek die ertoe doet
          </Typography>
        </Container>
      </Box>
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmSubmit}
        formData={formData}
        isSubmitting={isSubmitting}
      />

      {/* Success Modal */}
      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </Box>
  );
}

export default App;